<main>
  <loading-overlay [isLoading]="isLoading() > 0" />
  <p-toast position="top-center" />

  <div class="content">
    <div class="page__title">
      <div class="page__title_container">
        <h1>{{ 'Receipt_Title' | translateWF: 'Capture receipts' }}</h1>
        <div class="page__title_buttons">
          @if (owner()) {
            <p-button [label]="'Receipt_Upload' | translateWF: 'Upload files'" icon="pi pi-upload" variant="outlined" (onClick)="fileInput.click()" />
            <input #fileInput type="file" class="hidden" multiple [accept]="accept" (change)="uploadFiles($event)" />
            <p-button [label]="'Receipt_Add' | translateWF: 'Add receipt'" icon="pi pi-plus" (onClick)="addReceipt()" />
          }
          <p-button variant="outlined" (onClick)="back()">{{ 'Back' | translateWF: 'Back' }}</p-button>
        </div>
      </div>
      <p class="text-sm text-[var(--p-text-muted-color)]">
        {{ 'Receipt_Subtitle' | translateWF: 'Capture and save each receipt on its own. Capturing receipts posts nothing.' }}
      </p>
    </div>

    <p-panel [header]="'Receipt_Owner_Title' | translateWF: 'Receipts owner'" styleClass="mb-5">
      <p-card>
        <div class="grid grid-cols-12 gap-5 items-end">
          <div class="form-box col-span-12 md:col-span-8">
            <label for="receipt-owner">{{ 'Receipt_Owner_Label' | translateWF: 'Fund or trip' }} *</label>
            <p-select
              inputId="receipt-owner"
              [formControl]="ownerControl"
              [options]="ownerOptions()"
              optionLabel="label"
              optionValue="value"
              [filter]="true"
              [placeholder]="'Receipt_Owner_Placeholder' | translateWF: 'Select a fund or trip'"
              styleClass="w-full"
              (onChange)="onOwnerChange($event.value)"
            />
            <small class="text-surface-500">
              @if (owners().length === 0) {
                {{ 'Receipt_Owner_None' | translateWF: 'You hold no active fund and no open trip to capture receipts for.' }}
              } @else if (isFund()) {
                {{ 'Receipt_Owner_FundHint' | translateWF: 'Fund receipts are in the fund’s currency and go to its pool.' }}
              } @else if (owner()) {
                {{ 'Receipt_Owner_TripHint' | translateWF: 'Trip receipts can be in HNL or USD; totals show in USD.' }}
              }
            </small>
          </div>
          @if (owner() && receipts().length > 0 && moveOptions().length > 0) {
            <div class="col-span-12 md:col-span-4 flex justify-end">
              <p-button [label]="'Receipt_Move' | translateWF: 'Move receipts'" icon="pi pi-arrow-right-arrow-left" variant="outlined" (onClick)="openMove()" />
            </div>
          }
        </div>
      </p-card>
    </p-panel>

    @if (owner()) {
      <div class="cs-summary-grid">
        <p-card data-variant="summary" [header]="'Receipt_Kpi_Count' | translateWF: 'Receipts'">
          <ng-template pTemplate="subtitle">{{ kpis().count }}</ng-template>
          {{ ownerLabel() }}
        </p-card>
        <p-card data-variant="summary" [header]="'Receipt_Kpi_InPool' | translateWF: 'In pool'">
          <ng-template pTemplate="subtitle">{{ kpis().inPool }}</ng-template>
          {{ kpis().incomplete }} {{ 'Receipt_Kpi_Incomplete' | translateWF: 'incomplete' }}
        </p-card>
        <p-card data-variant="summary" [header]="'Receipt_Kpi_Isv' | translateWF: 'ISV'">
          <ng-template pTemplate="subtitle">{{ kpis().symbol }} {{ kpis().tax | number: '1.2-2' }}</ng-template>
          {{ 'Receipt_Kpi_Isv_Unit' | translateWF: 'on these receipts' }}
        </p-card>
        <p-card data-variant="summary" [header]="'Receipt_Kpi_Total' | translateWF: 'Total'">
          <ng-template pTemplate="subtitle">{{ kpis().symbol }} {{ kpis().total | number: '1.2-2' }}</ng-template>
          {{ 'Receipt_Kpi_Total_Unit' | translateWF: 'not posted yet' }}
        </p-card>
      </div>

      @if (editingId(); as editing) {
        <p-panel styleClass="mb-5">
          <ng-template #header>
            <div class="flex items-center gap-3">
              <span class="font-semibold">
                @if (editingReceipt(); as current) {
                  {{ 'Receipt_Editing' | translateWF: 'Editing receipt' }} {{ current.number }}
                } @else {
                  {{ 'Receipt_New' | translateWF: 'New receipt' }}
                }
              </span>
              @if (editingReceipt(); as current) {
                <p-tag [severity]="statusSeverity(current.status)" [value]="statusLabel(current.status)" />
              }
            </div>
          </ng-template>

          <p-card>
            <app-expense-receipt-editor
              [receipt]="editingReceipt()"
              [openKey]="editKey()"
              [ownerCurrency]="ownerCurrency()"
              [concepts]="concepts()"
              mode="capture"
              (saved)="saveReceipt($event)"
              (cancelled)="closeEditor()"
              (attach)="attachFile($event)"
              (removeAttachment)="removeAttachment($event)"
            />
          </p-card>
        </p-panel>
      }

      <p-panel [header]="'Receipt_List_Title' | translateWF: 'Captured receipts'">
        <p-card data-variant="compact">
          <p-table [value]="receipts()" dataKey="id" [rowHover]="true" [paginator]="true" [rows]="25" [rowsPerPageOptions]="[25, 50, 100]">
            <ng-template pTemplate="header">
              <tr>
                <th style="width: 13rem">{{ 'Receipt_Col_Number' | translateWF: 'Number' }}</th>
                <th>{{ 'Receipt_Field_Vendor' | translateWF: 'Vendor' }}</th>
                <th>{{ 'Receipt_Field_FiscalNumber' | translateWF: 'Fiscal number' }}</th>
                <th style="width: 8rem">{{ 'Receipt_Field_Date' | translateWF: 'Date' }}</th>
                <th>{{ 'Receipt_Field_Concept' | translateWF: 'Expense concept' }}</th>
                <th class="text-right" style="width: 6rem">{{ 'Receipt_Attachments' | translateWF: 'Attachments' }}</th>
                <th class="text-right" style="width: 10rem">{{ 'Receipt_Kpi_Total' | translateWF: 'Total' }}</th>
                <th style="width: 8rem">{{ 'Status' | translateWF: 'Status' }}</th>
                <th style="width: 7rem"></th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-rowData>
              @let row = getRow(rowData);
              <tr>
                <td class="font-mono">{{ row.number }}</td>
                <td>{{ row.vendor?.name ?? '—' }}</td>
                <td class="font-mono">{{ row.fiscalNumber ?? '—' }}</td>
                <td class="tabular-nums">{{ row.date ? (row.date | date: 'yyyy-MM-dd' : 'UTC') : '—' }}</td>
                <td>{{ firstConcept(row) }}</td>
                <td class="text-right">{{ row.attachments.length }}</td>
                <td class="text-right tabular-nums">{{ symbolOf(row.currency) }} {{ row.total | number: '1.2-2' }}</td>
                <td>
                  <p-tag [severity]="statusSeverity(row.status)" [value]="statusLabel(row.status)" [pTooltip]="row.rejectionComment ?? undefined" tooltipPosition="top" />
                </td>
                <td>
                  <div class="cs-table-actions justify-end">
                    <p-button icon="pi pi-pencil" [text]="true" [rounded]="true" (onClick)="editReceipt(row)" />
                    <p-button icon="pi pi-trash" severity="danger" [text]="true" [rounded]="true" (onClick)="confirmDelete(row)" />
                  </div>
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="9" class="text-center">{{ 'Receipt_Empty' | translateWF: 'No receipts in this capture. Add one or upload files.' }}</td>
              </tr>
            </ng-template>
          </p-table>
        </p-card>
      </p-panel>
    }

    <p-dialog
      [visible]="moveVisible()"
      (visibleChange)="moveVisible.set($event)"
      [modal]="true"
      [style]="{ width: '32rem' }"
      [header]="'Receipt_Move_Title' | translateWF: 'Change the receipts’ owner'"
    >
      <div class="form-box">
        <label for="receipt-move">{{ 'Receipt_Owner_Label' | translateWF: 'Fund or trip' }} *</label>
        <p-select
          inputId="receipt-move"
          [formControl]="moveTarget"
          [options]="moveOptions()"
          optionLabel="label"
          optionValue="value"
          [filter]="true"
          appendTo="body"
          styleClass="w-full"
        />
        <small class="text-surface-500">
          {{ 'Receipt_Move_Hint' | translateWF: 'Applies to every receipt of this capture. A fund only takes receipts in its currency.' }}
        </small>
      </div>
      <ng-template #footer>
        <p-button [label]="'Cancel' | translateWF: 'Cancel'" variant="outlined" (onClick)="moveVisible.set(false)" />
        <p-button [label]="'Receipt_Move_Apply' | translateWF: 'Apply'" [disabled]="!moveTarget.value" (onClick)="confirmMove()" />
      </ng-template>
    </p-dialog>

    <p-confirmDialog />
  </div>
</main>
